import type { ReactElement } from 'react';
import { Heart } from 'lucide-react';
import { formatNumber } from '@xdocs/i18n';
import type { DiscoverListRow } from '@xdocs/schemas';
import { Card, cn, useI18n } from '@xdocs/ui';
import { resolveCategoryLabel } from '../../features/search/categoryLabel.js';
import { useDiscoverT } from './useDiscoverT.js';
import styles from './DiscoverCard.module.css';

export interface DiscoverCardProps {
  row: DiscoverListRow;
  onOpen: (row: DiscoverListRow) => void;
}

/** One `/discover` gallery card (F62), matching design/app/25-discover.html:
 * flat-color cover (no kind icon overlay — mockup-25 covers are plain
 * gradient blocks) + category chip, title, owner, like count. Two gaps vs
 * the mockup remain, both from what `discoverListRow` actually carries (see
 * this task's returned issues list): (1) the like count is DISPLAY-ONLY —
 * no per-row `viewerReaction` field to toggle from here, unlike
 * `discover.get`'s `sharePublicView` (interactive reacting lives on the
 * detail page's `ReactionBar`); (2) no per-row "is the owner PRO" flag, so
 * the mockup's owner-row PRO badge is omitted here (present on
 * `discover.get`/`comment.list` rows, which DO carry it). */
export function DiscoverCard({ row, onOpen }: DiscoverCardProps): ReactElement {
  const { locale, t } = useI18n();
  const { t: tDiscover } = useDiscoverT();
  const primaryCategory = row.categories[0];

  return (
    <Card interactive className={cn(styles['card'])}>
      <button type="button" className={cn(styles['trigger'])} onClick={() => onOpen(row)}>
        <div className={cn(styles['cover'])}>
          {primaryCategory && <span className={cn(styles['categoryChip'])}>{resolveCategoryLabel(t, primaryCategory)}</span>}
        </div>
        <div className={cn(styles['body'])}>
          <p className={cn(styles['title'])}>{row.title}</p>
          <div className={cn(styles['footer'])}>
            <div className={cn(styles['owner'])}>
              <span className={cn(styles['ownerInitial'])} aria-hidden="true">
                {(row.ownerDisplayName ?? '?').charAt(0).toUpperCase()}
              </span>
              <span className={cn(styles['ownerName'])}>{tDiscover('discover:card.by', { name: row.ownerDisplayName ?? '—' })}</span>
            </div>
            <span className={cn(styles['likes'])}>
              <Heart size={14} aria-hidden="true" />
              {formatNumber(locale, row.likeCount, { notation: 'compact' })}
            </span>
          </div>
        </div>
      </button>
    </Card>
  );
}
